import React, { useState, useEffect } from "react";
import { Table, Card, Tag, Space, DatePicker, Select, Button, Row, Col, Empty, Tooltip } from "antd";
import { ReloadOutlined, FilterOutlined, UserOutlined, ClockCircleOutlined, EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * 数据录入历史记录组件
 * 显示飞机配置和值班人员的数据修改历史
 */
const DataEntryHistory = ({ dataSource = [], loading = false, onRefresh }) => {
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        dataType: null,
        operation: null,
        dateRange: null,
    });

    // 数据类型选项
    const dataTypeOptions = [
        { value: "aircraft_config", label: "飞机配置", color: "blue" },
    ];

    // 操作类型选项和颜色映射
    const operationOptions = [
        { value: "insert", label: "新增", color: "success" },
        { value: "update", label: "修改", color: "warning" },
        { value: "delete", label: "删除", color: "error" },
    ];

    const operationColorMap = {
        insert: "success",
        update: "warning",
        delete: "error",
    };

    const operationIconMap = {
        insert: <PlusOutlined />,
        update: <EditOutlined />,
        delete: <DeleteOutlined />,
    };

    // 应用筛选
    useEffect(() => {
        let filtered = [...dataSource];

        // 按数据类型筛选
        if (filters.dataType) {
            filtered = filtered.filter(item => item.data_type === filters.dataType);
        }

        // 按操作类型筛选
        if (filters.operation) {
            filtered = filtered.filter(item => item.operation === filters.operation);
        }

        // 按日期范围筛选
        if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
            const startDate = dayjs(filters.dateRange[0]).startOf("day");
            const endDate = dayjs(filters.dateRange[1]).endOf("day");
            filtered = filtered.filter(item => {
                const itemDate = dayjs(item.created_at);
                return itemDate.isAfter(startDate) && itemDate.isBefore(endDate);
            });
        }

        setFilteredData(filtered);
    }, [dataSource, filters]);

    // 重置筛选
    const handleResetFilters = () => {
        setFilters({
            dataType: null,
            operation: null,
            dateRange: null,
        });
    };

    // 格式化变更内容
    const formatChangeContent = (record) => {
        const { data_type, operation, old_value, new_value } = record;

        if (operation === "insert") {
            return renderValueDetails(new_value, data_type);
        } else if (operation === "delete") {
            return renderValueDetails(old_value, data_type);
        } else if (operation === "update") {
            return (
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                    <div>
                        <Tag color="red" style={{ minWidth: 50 }}>修改前</Tag>
                        {renderValueDetails(old_value, data_type)}
                    </div>
                    <div>
                        <Tag color="green" style={{ minWidth: 50 }}>修改后</Tag>
                        {renderValueDetails(new_value, data_type)}
                    </div>
                </Space>
            );
        }
    };

    // 渲染值的详细信息
    const renderValueDetails = (value, dataType) => {
        if (!value) return <span style={{ color: "#999" }}>-</span>;

        try {
            const data = typeof value === "string" ? JSON.parse(value) : value;

            if (dataType === "aircraft_config") {
                return (
                    <Space size="small" wrap>
                        <Tag>注册号: {data.aircraft_registration}</Tag>
                        <Tag>计划小时: {data.planned_hours}h</Tag>
                        <Tag>检查日期: {data.next_inspection_date}</Tag>
                    </Space>
                );
            }
        } catch (e) {
            return <span style={{ color: "#999" }}>{String(value)}</span>;
        }
    };

    const columns = [
        {
            title: "操作时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 180,
            render: (time) => (
                <Tooltip title={dayjs(time).format("YYYY-MM-DD HH:mm:ss")}>
                    <Space>
                        <ClockCircleOutlined style={{ color: "#1890ff" }} />
                        <span>{dayjs(time).format("MM-DD HH:mm")}</span>
                    </Space>
                </Tooltip>
            ),
            sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
            defaultSortOrder: "descend",
        },
        {
            title: "数据类型",
            dataIndex: "data_type",
            key: "data_type",
            width: 120,
            render: (type) => {
                const option = dataTypeOptions.find(opt => opt.value === type);
                return <Tag color={option?.color}>{option?.label || type}</Tag>;
            },
        },
        {
            title: "操作类型",
            dataIndex: "operation",
            key: "operation",
            width: 100,
            render: (operation) => {
                const option = operationOptions.find(opt => opt.value === operation);
                return (
                    <Tag color={operationColorMap[operation]} icon={operationIconMap[operation]}>
                        {option?.label || operation}
                    </Tag>
                );
            },
        },
        {
            title: "变更内容",
            key: "content",
            render: (_, record) => formatChangeContent(record),
        },
        {
            title: "操作人",
            dataIndex: "created_by_user_id",
            key: "created_by_user_id",
            width: 120,
            render: (userId) => userId ? (
                <Space>
                    <UserOutlined style={{ color: "#52c41a" }} />
                    <span>ID: {userId}</span>
                </Space>
            ) : (
                <span style={{ color: "#999" }}>-</span>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <span>数据录入历史</span>
                    <Tag color="blue">{filteredData.length} 条记录</Tag>
                </Space>
            }
            extra={
                <Button
                    icon={<ReloadOutlined spin={loading} />}
                    onClick={onRefresh}
                    loading={loading}
                    size="small"
                >
                    刷新
                </Button>
            }
        >
            {/* 筛选区域 */}
            <Card
                size="small"
                style={{ marginBottom: 16, background: "#fafafa" }}
                bodyStyle={{ padding: "12px 16px" }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                        <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <span style={{ fontSize: 12, color: "#999" }}>数据类型</span>
                            <Select
                                placeholder="全部类型"
                                allowClear
                                value={filters.dataType}
                                onChange={(value) => setFilters({ ...filters, dataType: value })}
                                style={{ width: "100%" }}
                                size="small"
                            >
                                {dataTypeOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>
                                        <Tag color={opt.color}>{opt.label}</Tag>
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <span style={{ fontSize: 12, color: "#999" }}>操作类型</span>
                            <Select
                                placeholder="全部操作"
                                allowClear
                                value={filters.operation}
                                onChange={(value) => setFilters({ ...filters, operation: value })}
                                style={{ width: "100%" }}
                                size="small"
                            >
                                {operationOptions.map(opt => (
                                    <Option key={opt.value} value={opt.value}>
                                        <Tag color={opt.color} icon={operationIconMap[opt.value]}>
                                            {opt.label}
                                        </Tag>
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Space direction="vertical" size={4} style={{ width: "100%" }}>
                            <span style={{ fontSize: 12, color: "#999" }}>日期范围</span>
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                                format="YYYY-MM-DD"
                                style={{ width: "100%" }}
                                size="small"
                            />
                        </Space>
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Button
                            icon={<FilterOutlined />}
                            onClick={handleResetFilters}
                            block
                            size="small"
                            style={{ marginTop: 20 }}
                        >
                            重置筛选
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* 历史记录表格 */}
            <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                rowKey="id"
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total) => `共 ${total} 条记录`,
                }}
                size="small"
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <p style={{ marginBottom: 8 }}>暂无历史记录</p>
                                    <p style={{ color: "#999", fontSize: 12 }}>
                                        数据的创建、修改和删除操作将自动记录在此
                                    </p>
                                </div>
                            }
                        />
                    ),
                }}
            />
        </Card>
    );
};

export default DataEntryHistory;


